import {
  LTLQuickQuoteLineItem,
  LTLQuickQuoteRequest,
  LTLQuickQuoteStop,
} from 'lib/api/getLTLQuickQuotes';
import {
  TURVO_PAYMENT_TERM_NONE,
  getTurvoHandlingUnitTypeName,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/Turvo/constants';
import {
  LTL_UNIT_SYSTEMS,
  PACKAGE_TYPE_TO_CODE,
  QuickQuoteInputs,
  QuoteCountries,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { TransportType } from 'types/QuoteRequest';
import { Maybe } from 'types/UtilityTypes';

import {
  LTLCustomerData,
  normalizeNumericValue,
  roundToTwoDecimals,
} from './ltlRequestShared';

export type { LTLCustomerData };

/**
 * Builds the LTL Quick Quote API request payload for Turvo.
 *
 */
export function buildLTLTurvoRequest(
  formValues: QuickQuoteInputs,
  ltlCustomer: Maybe<LTLCustomerData>,
  ltlQuoteRequestId: number,
  emailId: number,
  threadId: string
): LTLQuickQuoteRequest {
  const rawPaymentTerm = formValues.ltl?.paymentTerm ?? '';
  const paymentTerm =
    rawPaymentTerm === TURVO_PAYMENT_TERM_NONE ? '' : rawPaymentTerm;
  const isMetric = formValues.ltl?.commodity?.units === LTL_UNIT_SYSTEMS.METRIC;
  const unitValue = isMetric ? 1 : 0;

  const toInches = (n: number) => (isMetric ? n / 2.54 : n);
  const toLbs = (n: number) => (isMetric ? n * 2.20462 : n);

  const lineItems: LTLQuickQuoteLineItem[] = (formValues.ltl?.items ?? []).map(
    (item) => {
      const handlingUnits = item.handlingUnits ?? 1;
      const hasDims =
        (item.length ?? 0) > 0 &&
        (item.width ?? 0) > 0 &&
        (item.height ?? 0) > 0;

      const L = toInches(item.length ?? 0);
      const W = toInches(item.width ?? 0);
      const H = toInches(item.height ?? 0);

      const cubicFeet = hasDims
        ? roundToTwoDecimals(((L * W * H) / 1728) * handlingUnits)
        : 0;
      const cubicMeters = roundToTwoDecimals(cubicFeet * 0.0283168466);

      const weightLbs = toLbs(item.weight ?? 0);
      const poundsPerCubicFeet =
        cubicFeet > 0 && weightLbs > 0
          ? roundToTwoDecimals(weightLbs / cubicFeet)
          : 0;

      const handlingUnitTypeName = getTurvoHandlingUnitTypeName(
        item.handlingUnitType
      );

      return {
        packageType: item.packageType ?? PACKAGE_TYPE_TO_CODE['Pallet'],
        freightClass:
          item.freightClass ?? formValues.ltl?.commodity?.freightClass ?? '',
        classManuallySelected: false,
        handlingUnits,
        handlingUnitTypeName,
        totalWeight: item.weight ?? 0,
        commodityName: item.description ?? '',
        commodityExternalTmsId: item.commodityExternalTmsId ?? '',
        commodityDescription: item.description ?? '',
        quantityOfGoods: item.pieces ?? 1,
        length: item.length,
        width: item.width,
        height: item.height,
        cubicFeet,
        cubicMeters,
        poundsPerCubicFeet,
        nmfcNumber: item.nmfcCode ?? '',
      };
    }
  );

  const stops: LTLQuickQuoteStop[] = formValues.stops.map((stop, index) => {
    const isFirstStop = index === 0;
    const isLastStop = index === formValues.stops.length - 1;

    const address = {
      name: stop.addressName ?? '',
      addressLine1: stop.addressLine1 ?? '',
      addressLine2: stop.addressLine2 ?? '',
      city: stop.city ?? '',
      state: stop.state ?? '',
      zip: stop.zip ?? '',
      country: stop.country ?? QuoteCountries.USA,
    };

    const baseStop: LTLQuickQuoteStop = {
      order: index,
      stopType: isFirstStop ? StopTypes.Pickup : StopTypes.Dropoff,
      address,
      // Keep geosearch prefixes ("tloc:"/"gplace:") intact, and also pass
      // free-text entries when user doesn't select a dropdown option.
      externalTMSID: stop.externalTMSID || stop.location || undefined,
    };

    if (isFirstStop && formValues.pickupDate) {
      return {
        ...baseStop,
        readyTime: formValues.pickupDate.toISOString(),
      };
    }

    if (isLastStop && formValues.deliveryDate) {
      return {
        ...baseStop,
        mustDeliver: formValues.deliveryDate.toISOString(),
      };
    }

    return baseStop;
  });

  const declaredValue = normalizeNumericValue(
    formValues.ltl?.value?.declaredValue
  );
  const deductibleValue = normalizeNumericValue(
    formValues.ltl?.value?.deductibleValue
  );

  const customerData = {
    name: ltlCustomer?.customerName ?? '',
    externalTMSID: ltlCustomer?.customerId ?? '',
  };

  return {
    emailId,
    threadId,
    quoteRequestId: ltlQuoteRequestId,
    // Turvo LTL loads always use VAN as the transport type.
    transportType: TransportType.VAN,
    customerExternalTmsId: ltlCustomer?.customerId ?? formValues.customerName,
    stops,
    customer: customerData,
    lineItems,
    accessorials: formValues.ltl?.selectedAccessorials ?? [],
    weightUnit: unitValue,
    dimensionUnit: unitValue,
    linearFeet: formValues.ltl?.commodity?.linearFeet,
    searchByTariff: false,
    stackable: formValues.ltl?.commodity?.stackable ?? false,
    isFrontOffice: true,
    shipmentType: 2,
    actualShipmentType: 2,
    trailerType: 0,
    pcmilerRoutingType: 0,
    declaredValue: declaredValue.toString(),
    deductibleValue,
    transportationType: formValues.ltl?.serviceType ?? 'any',
    paymentTerm,
    pickupServices: formValues.ltl?.pickupServices ?? [],
    deliveryServices: formValues.ltl?.deliveryServices ?? [],
  };
}
